import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

import { Icono } from '../icono/icono';

/** Marca de corte entre bloques de números. */
export const ELIPSIS = '..' as const;

export type ItemPaginador = number | typeof ELIPSIS;

/**
 * Paginador del Figma (`Paginator`, `244:3290`): flechas + números con elipsis.
 *
 * Portado de `PaginationComponent` del backoffice
 * (`src/components/pagination/`). El algoritmo de elisión se conserva tal cual
 * para que ambos frontends paginen igual; lo que cambia es la carcasa:
 *
 * - `@Input()/@Output()` → `input()` / `output()` (el público es signals).
 * - El backoffice arma cada número con su `ButtonComponent` (`app-button`),
 *   que acá no existe: se usan botones planos y el `app-icono` propio.
 * - La variante `type="dot"` no se porta: en el público los puntos del
 *   carrusel los dibuja la galería de la ficha, no el paginador.
 *
 * El `track $index` no es casual: con muchas páginas la lista trae **dos**
 * elipsis, y trackear por valor colisiona (NG0955).
 */
@Component({
  selector: 'app-paginador',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icono],
  templateUrl: './paginador.html',
  styleUrl: './paginador.scss',
})
export class Paginador {
  readonly paginaActual = input.required<number>();
  readonly totalPaginas = input.required<number>();
  /** Cuántos números como máximo antes de recortar con elipsis. */
  readonly maxNumeros = input(6);

  readonly cambioPagina = output<number>();

  readonly elipsis = ELIPSIS;

  /** Con una sola página no hay nada que paginar. */
  readonly visible = computed(() => this.totalPaginas() > 1);

  readonly paginas = computed<ItemPaginador[]>(() => {
    const total = this.totalPaginas();
    const actual = this.paginaActual();
    const maximo = this.maxNumeros();

    if (total <= maximo) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // Primera y última siempre presentes; el bloque central se centra en la
    // página actual y se corre cuando toca alguno de los bordes.
    const items: ItemPaginador[] = [1, total];
    const mitad = (maximo - 3) / 2;
    let inicio = actual - Math.floor(mitad);
    let fin = actual + Math.ceil(mitad);

    const sobraInicio = inicio < 2 ? 2 - inicio : 0;
    const sobraFin = fin > total - 1 ? fin - (total - 1) : 0;
    inicio -= sobraFin;
    fin += sobraInicio;
    inicio = Math.max(inicio, 2);
    fin = Math.min(fin, total - 1);

    if (inicio > 2) items.splice(1, 0, ELIPSIS);
    for (let i = inicio; i <= fin; i++) {
      items.splice(items.length - 1, 0, i);
    }
    if (fin < total - 1) items.splice(items.length - 1, 0, ELIPSIS);

    return items;
  });

  irA(pagina: number): void {
    if (pagina === this.paginaActual()) return;
    if (pagina < 1 || pagina > this.totalPaginas()) return;
    this.cambioPagina.emit(pagina);
  }

  anterior(): void {
    this.irA(this.paginaActual() - 1);
  }

  siguiente(): void {
    this.irA(this.paginaActual() + 1);
  }
}
